import { Api } from "./api";
import {
  getDivisionText,
  loadParagraphs,
  loadTrackedChanges,
} from "src/services/paragraph-service";
import { loadCharStyleRecords, loadStyleRecords } from "src/services";

import type { CharacterStyleRecord, ParagraphStyleRecord } from "src/services";
import type { ParagraphInfo } from "src/types";

export const wordApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getCharacterStyleRecords: builder.query<CharacterStyleRecord[], void>({
      queryFn: async () => {
        try {
          const characterStyleRecords = await loadCharStyleRecords();
          let prevUniqueKey = "";
          let newUniqueKey = "";
          return {
            data: characterStyleRecords
              .filter((r) => {
                newUniqueKey = `${r.paragraphTag}-${r.rangeStart}-${r.rangeEnd}`;
                if (newUniqueKey !== prevUniqueKey) {
                  prevUniqueKey = newUniqueKey;
                  return true;
                }
                return false;
              })
              .sort((r1, r2) => {
                const paragraphIndexDiff =
                  Number(r1.id.split(":")[2]) - Number(r2.id.split(":")[2]);
                return paragraphIndexDiff || r1.rangeStart - r2.rangeStart;
              }),
          };
        } catch (err) {
          console.error("wordApi.getCharacterStyleRecords: ", err);
          throw err;
        }
      },
      providesTags: ["character-style-records"],
    }),
    getDivisionText: builder.query<Record<string, string>, ParagraphInfo[]>({
      queryFn: async (paragraphInfo) => {
        try {
          const chapterText = await getDivisionText(paragraphInfo);
          return { data: chapterText };
        } catch (err) {
          console.error("wordApi.getParagraphs: ", err);
          throw err;
        }
      },
    }),
    getParagraphs: builder.query<Pick<Word.Paragraph, "style" | "uniqueLocalId">[], void>({
      queryFn: async () => {
        try {
          const paragraphs = await loadParagraphs();
          return {
            data: paragraphs.map(({ style, uniqueLocalId }) => ({
              style,
              uniqueLocalId,
            })),
          };
        } catch (err) {
          console.error("wordApi.getParagraphs: ", err);
          throw err;
        }
      },
      providesTags: ["paragraphs"],
    }),
    getStyleRecords: builder.query<ParagraphStyleRecord[], void>({
      queryFn: async () => {
        try {
          const styleRecords = await loadStyleRecords();
          return { data: styleRecords };
        } catch (err) {
          console.error("wordApi.getStyleRecords: ", err);
          throw err;
        }
      },
      providesTags: ["paragraph-style-records"],
    }),
    getTrackedChanges: builder.query<Record<string, number>, void>({
      queryFn: async () => {
        try {
          const trackedChanges = await loadTrackedChanges();
          return { data: trackedChanges };
        } catch (err) {
          console.error("wordApi.trackedChanges: ", err);
          throw err;
        }
      },
      providesTags: ["tracked-changes"],
    }),
  }),
});

export const {
  useGetCharacterStyleRecordsQuery,
  useGetDivisionTextQuery,
  useGetParagraphsQuery,
  useGetStyleRecordsQuery,
  useGetTrackedChangesQuery,
} = wordApi;
